import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CheckCircle2, Loader2, Mail, Shield, ShieldCheck, X,
} from 'lucide-react';
import { eisfSigning } from '@/api/client';
import { errorText } from '@/lib/permissionError';

/**
 * In-app signing wizard for eISF approvers. Mirrors the eConsent 3-step
 * pattern:
 *   1. Verify identity — server emails a 6-digit OTP, user enters it.
 *   2. Sign — DocuSeal embed iframe rendered directly in the modal.
 *   3. Done — success + prompt to close. The DocuSeal webhook flips the
 *      routing row and the "Signed" badge appears back on the doc detail
 *      once the webhook applier runs.
 *
 * The OTP is the §11.200 second factor (username+password/MFA already
 * satisfied the first factor at login). Same substrate as DoA row
 * signing — different endpoint, same UX.
 */

export interface SignApprovalDialogProps {
  versionId: string;
  versionLabel: string;
  documentTitle: string;
  workflowName: string | null;
  open: boolean;
  onClose: () => void;
  onSigned?: () => void;
}

type Step = 'otp' | 'iframe' | 'done';

export function SignApprovalDialog({
  versionId, versionLabel, documentTitle, workflowName, open, onClose, onSigned,
}: SignApprovalDialogProps) {
  const [step, setStep] = useState<Step>('otp');
  const [challengeToken, setChallengeToken] = useState<string | null>(null);
  const [maskedDest, setMaskedDest] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const finalizeTriggeredRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setStep('otp');
      setChallengeToken(null);
      setMaskedDest(null);
      setOtpCode('');
      setEmbedUrl(null);
      finalizeTriggeredRef.current = false;
    }
  }, [open]);

  // Poll DocuSeal directly for completion — the webhook that would normally
  // flip the routing row can't reach localhost in dev, and can silently go
  // stale in any env (e.g. if the API Gateway it points at is ever
  // recreated). Mirrors the eConsent poll+finalize fallback.
  const syncStatusQ = useQuery({
    queryKey: ['eisf', 'sign-sync-status', versionId],
    queryFn: () => eisfSigning.syncStatus(versionId),
    enabled: step === 'iframe',
    refetchInterval: 5000,
  });

  const finalizeMut = useMutation({
    mutationFn: () => eisfSigning.finalizeSync(versionId),
    onSuccess: (r) => {
      if (r.synced_count > 0) {
        setStep('done');
        onSigned?.();
      }
    },
  });

  useEffect(() => {
    if (step !== 'iframe') return;
    if (!syncStatusQ.data?.any_newly_completed) return;
    if (finalizeTriggeredRef.current || finalizeMut.isPending) return;
    finalizeTriggeredRef.current = true;
    finalizeMut.mutate(undefined, {
      onSettled: () => { finalizeTriggeredRef.current = false; },
    });
  }, [step, syncStatusQ.data, finalizeMut]);

  const startMut = useMutation({
    mutationFn: () => eisfSigning.startOtp(versionId),
    onSuccess: (r) => {
      setChallengeToken(r.challenge_token);
      setMaskedDest(r.destination_masked);
      toast.success(`Code sent to ${r.destination_masked}`);
    },
    onError: (err) => toast.error(errorText(err, 'Failed to send code')),
  });

  const verifyMut = useMutation({
    mutationFn: () => {
      if (!challengeToken) throw new Error('No challenge in flight');
      return eisfSigning.verifyOtp(versionId, { challenge_token: challengeToken, otp_code: otpCode });
    },
    onSuccess: (r) => {
      setEmbedUrl(r.embed_url);
      setStep('iframe');
    },
    onError: (err) => toast.error(errorText(err, 'Verification failed')),
  });

  // Best-effort: some DocuSeal versions post a completion message to the
  // parent window. Harmless to keep listening, but the poll+finalize
  // above (not this) is what reliably advances the step — this self-hosted
  // instance doesn't actually emit one.
  useEffect(() => {
    if (step !== 'iframe') return;
    function onMessage(e: MessageEvent) {
      const type = (e.data as { event?: string; type?: string } | null)?.event
        ?? (e.data as { event?: string; type?: string } | null)?.type;
      if (typeof type === 'string' && /completed|signed|finish/i.test(type)) {
        setStep('done');
        onSigned?.();
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [step, onSigned]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-primary/60 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b border-surface-border flex items-center justify-between shrink-0">
          <div>
            <div className="text-sm font-semibold text-ink-primary">Sign document</div>
            <div className="text-[11px] text-ink-muted mt-0.5">
              {documentTitle} · {versionLabel} {workflowName ? `· ${workflowName}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-muted/40"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-5 py-3 border-b border-surface-border bg-surface-muted/20 shrink-0">
          <ol className="flex items-center gap-3 text-xs">
            <StepPill n={1} active={step === 'otp'} done={step !== 'otp'} label="Verify" />
            <div className="flex-1 h-px bg-surface-border" />
            <StepPill n={2} active={step === 'iframe'} done={step === 'done'} label="Sign" />
            <div className="flex-1 h-px bg-surface-border" />
            <StepPill n={3} active={step === 'done'} done={false} label="Done" />
          </ol>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {step === 'otp' && (
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3 rounded border border-surface-border bg-surface-muted/30 p-3">
                <Shield className="h-5 w-5 text-primary-600 mt-0.5" />
                <div className="text-xs text-ink-primary">
                  <div className="font-medium">Verify your identity</div>
                  <p className="mt-1 text-ink-secondary">
                    21 CFR Part 11 requires a second factor before you sign.
                    Click below to email a 6-digit code to your work address.
                  </p>
                </div>
              </div>

              {!challengeToken ? (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => startMut.mutate()}
                    disabled={startMut.isPending}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    {startMut.isPending
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <Mail className="h-4 w-4" />}
                    Send verification code
                  </button>
                </div>
              ) : (
                <div className="space-y-3 text-center">
                  <div className="text-xs text-ink-secondary">
                    Code sent to <span className="font-mono text-ink-primary">{maskedDest}</span>.
                    Didn&apos;t get it? <button
                      type="button"
                      onClick={() => startMut.mutate()}
                      className="underline text-primary-700 hover:text-primary-800"
                    >
                      Resend
                    </button>
                  </div>
                  <div>
                    <label className="label text-xs" htmlFor="sign-otp">6-digit code</label>
                    <input
                      id="sign-otp"
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="form-input font-mono tracking-widest text-lg text-center max-w-[180px]"
                      placeholder="000000"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => verifyMut.mutate()}
                    disabled={verifyMut.isPending || otpCode.length !== 6}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    {verifyMut.isPending
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <ShieldCheck className="h-4 w-4" />}
                    Verify &amp; continue
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 'iframe' && embedUrl && (
            <iframe
              title="Signature"
              src={embedUrl}
              className="flex-1 w-full border-0"
              allow="camera; microphone; clipboard-read; clipboard-write"
            />
          )}

          {step === 'done' && (
            <div className="p-8 text-center space-y-3">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
              <div className="text-base font-semibold text-ink-primary">Signature captured</div>
              <p className="text-xs text-ink-muted max-w-md mx-auto">
                Your signature has been recorded. This document will update automatically in a
                few seconds. It is safe to close this window.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="btn-primary inline-flex items-center gap-2"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepPill({ n, active, done, label }: { n: number; active: boolean; done: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2 shrink-0">
      <span
        className={
          'inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold ' +
          (done
            ? 'bg-emerald-500 text-white'
            : active
              ? 'bg-primary-600 text-white'
              : 'bg-surface-muted text-ink-muted')
        }
      >
        {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : n}
      </span>
      <span className={active ? 'font-medium text-ink-primary' : 'text-ink-muted'}>{label}</span>
    </li>
  );
}
